import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Archive as ArchiveIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '80vw',
    height: '80vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const ZipContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const InfoPanel = styled(Card)(({ theme }) => ({
  width: '300px',
  margin: '16px 0 16px 16px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    width: 'auto',
    margin: '12px',
  },
}));

const FileListPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  margin: '16px 16px 16px 8px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    margin: '0 12px 12px 12px',
  },
}));

const FileListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  '& .MuiListItem-root': {
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.05)',
    },
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '& .MuiChip-root': {
    justifyContent: 'flex-start',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const ZipViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: zipData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.zip_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedFile(null);
    }
  }, [open, eventId, filePath]);

  const downloadZip = () => {
    if (zipData) {
      // Create a text file with the ZIP contents listing
      const content = `ZIP Archive Contents: ${filename}\n\n${zipData.files.map(file => 
        `${file.path} (${formatSize(file.size)})`
      ).join('\n')}`;
      
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'archive'}_contents.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  // Get file extension
  const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  // Filter files based on search term
  const filteredFiles = zipData?.files?.filter(file =>
    file.path.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Calculate statistics
  const getStats = () => {
    if (!zipData || !zipData.files) return { totalFiles: 0, totalSize: 0, folders: 0 };
    
    const totalFiles = zipData.files.length;
    const totalSize = zipData.files.reduce((acc, file) => acc + (file.size || 0), 0);
    const folders = zipData.files.filter(file => file.is_directory).length;
    
    return { totalFiles, totalSize, folders };
  };

  const stats = getStats();

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <ArchiveIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.zip_viewer_title')} - ${filename}` : t('viewer.zip_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadZip} 
            disabled={!zipData}
            size="small"
            title={t('viewer.download_zip_listing')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {zipData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${stats.totalFiles} files`}
              size="small"
              variant="outlined"
            />
            <Chip 
              label={formatSize(stats.totalSize)}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <ZipContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_zip')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_zip')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : zipData ? (
            <>
              {/* Info Panel */}
              <InfoPanel>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Archive Info
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <StatsContainer>
                    <Chip 
                      label={`${stats.totalFiles} total files`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip 
                      label={`${stats.folders} folders`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip 
                      label={`${formatSize(stats.totalSize)} total size`}
                      variant="outlined"
                      size="small"
                    />
                    {zipData.compression_ratio && (
                      <Chip 
                        label={`${(zipData.compression_ratio * 100).toFixed(1)}% compression`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </StatsContainer>
                  
                  {selectedFile && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Selected File
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        {selectedFile.path}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Size: {formatSize(selectedFile.size)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modified: {formatDate(selectedFile.modified_date)}
                      </Typography>
                      {selectedFile.compressed_size && (
                        <Typography variant="body2" color="text.secondary">
                          Compressed: {formatSize(selectedFile.compressed_size)}
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </InfoPanel>

              {/* File List Panel */}
              <FileListPanel>
                <SearchContainer>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('viewer.search_files_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </SearchContainer>
                
                <FileListContainer>
                  <List dense>
                    {filteredFiles.map((file, index) => (
                      <ListItem 
                        key={index}
                        button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Create a clean copy of the file object to avoid circular references
                          const cleanFile = {
                            path: file.path,
                            size: file.size,
                            compressed_size: file.compressed_size,
                            modified_date: file.modified_date,
                            is_directory: file.is_directory
                          };
                          setSelectedFile(cleanFile);
                        }}
                        selected={selectedFile?.path === file.path}
                      >
                        <ListItemIcon>
                          {file.is_directory ? (
                            <FolderIcon color="primary" />
                          ) : (
                            <FileIcon color="action" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={file.path}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{formatSize(file.size)}</span>
                              {file.modified_date && (
                                <>
                                  <span>•</span>
                                  <span>{formatDate(file.modified_date)}</span>
                                </>
                              )}
                            </Box>
                          }
                        />
                        {!file.is_directory && (
                          <ListItemSecondaryAction>
                            <Chip 
                              label={getFileExtension(file.path)}
                              size="small"
                              variant="outlined"
                            />
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                    
                    {filteredFiles.length === 0 && searchTerm && (
                      <ListItem>
                        <ListItemText
                          primary={t('viewer.no_files_found')}
                          secondary={t('viewer.try_different_search')}
                        />
                      </ListItem>
                    )}
                  </List>
                </FileListContainer>
              </FileListPanel>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_zip_content')}
              </Typography>
            </Box>
          )}
        </ZipContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default ZipViewer;
